import {
  CherryAccountStatus,
  LedgerEntryType,
  ServiceCatalogDiscount,
  ServiceCatalogService,
  TreatmentPaymentLedgerEntry,
  TreatmentPaymentOrder,
  TreatmentPaymentOrderPaymentIntent,
} from '@/components/Timeline/types';
import { formatDate } from '@/services/date';
import { formatDollars } from '@/services/money';
import { Patient } from '@medplum/fhirtypes';
import { normalizePhoneNumber } from '@bariendo/lib/phone-validations';

/**
 * What the backend will silently drop from a discount selection.
 *
 * `PaymentService.prepareCheckoutSessionInput` keeps only the **largest-amount
 * discount per `DiscountType`** and discards the rest — it carries a
 * `TODO: Update to support multiple discount IDs when payment service allows it`.
 * Discounts therefore do not stack, and nothing in the response says which ones
 * were thrown away. A rep selecting three order-level discounts otherwise gets no
 * indication that only one applied.
 *
 * Grouped by the raw `type` string rather than a hardcoded enum: the rule is
 * "largest per type" whatever the types happen to be, so this stays correct if
 * the backend adds one.
 */
export interface DiscountConflicts {
  /** Strictly smaller than the largest of their type — certainly dropped. */
  ignored: string[];
  /**
   * Tied for the largest of their type, with more than one tied. Exactly one
   * applies; which one is not determinable here, because the backend breaks ties
   * on its own iteration order (strict `>`), not on anything we send.
   */
  tied: string[];
}

export function findDiscountConflicts(selectedNames: string[], catalog: ServiceCatalogDiscount[]): DiscountConflicts {
  const selected = selectedNames
    .map((name) => catalog.find((discount) => discount.name === name))
    .filter((discount): discount is ServiceCatalogDiscount => !!discount);

  const byType = new Map<string, ServiceCatalogDiscount[]>();
  for (const discount of selected) {
    const group = byType.get(discount.type) ?? [];
    group.push(discount);
    byType.set(discount.type, group);
  }

  const ignored: string[] = [];
  const tied: string[] = [];

  for (const group of byType.values()) {
    if (group.length < 2) {
      continue;
    }

    const largest = Math.max(...group.map((discount) => discount.amount));
    const atLargest = group.filter((discount) => discount.amount === largest);

    for (const discount of group) {
      if (discount.amount < largest) {
        ignored.push(discount.name);
      }
    }

    if (atLargest.length > 1) {
      tied.push(...atLargest.map((discount) => discount.name));
    }
  }

  return { ignored, tied };
}

/**
 * `Discount.type` in the payments DB. Order-level versus per-line-item — not
 * percentage versus fixed; both carry a flat dollar `amount`.
 */
const ORDER_DISCOUNT = 'CHECKOUT_SESSION';
const LINE_ITEM_DISCOUNT = 'CHECKOUT_SESSION_LINE_ITEM';

export interface OrderEstimate {
  /** Sum of the service's active prices, before any discount. */
  retail: number;
  /** What the selected discounts take off, after the largest-per-type rule. */
  discount: number;
  /** `retail - discount`, floored at zero. */
  total: number;
}

/** The largest amount of one discount type among the selection, or 0. */
function largestOfType(selected: ServiceCatalogDiscount[], type: string): number {
  return selected.reduce(
    (largest, discount) => (discount.type === type ? Math.max(largest, discount.amount) : largest),
    0
  );
}

/**
 * What the order *will* cost, computed from the catalog before it exists.
 *
 * **This is an estimate and the UI must say so.** The authoritative figure is
 * whatever the backend puts on the order; this exists only so a rep sees the
 * discount move the number while they are still choosing (BAR-2525), instead of
 * staring at the previous order's total.
 *
 * It mirrors payment-backend exactly, and the mirror is the fragile part:
 *
 * - Every active price on the product becomes its own line item at quantity 1
 *   (`prepareCheckoutSessionInput` maps `prices` straight to `lineItems`), so a
 *   product with two active prices is charged for both.
 * - `lineItem.amount_total = max(unitAmount - largest line-item discount, 0)`.
 * - `amount_total = max(sum(lineItem.amount_total) - largest order discount, 0)`.
 * - Only the **largest discount per type** survives — see `findDiscountConflicts`.
 *
 * Two things it deliberately cannot see, both of which make it low rather than
 * wrong in a way that overstates a refund: the `ESG_500_PROMO` auto-promo, which
 * payment-backend applies server-side and which *overrides* the caller's
 * discount on eligible products; and a price deactivated between the catalog
 * read and the create.
 *
 * Returns `null` when there is nothing honest to say — no service chosen, the
 * service is not in the catalog, or it carries no active price. A `$0.00`
 * estimate for an unpriced product would read as "free".
 */
export function estimateOrderTotal(
  serviceName: string | null,
  discountNames: string[],
  services: ServiceCatalogService[],
  discounts: ServiceCatalogDiscount[]
): OrderEstimate | null {
  const service = serviceName ? services.find((candidate) => candidate.name === serviceName) : undefined;
  if (!service?.prices.length) {
    return null;
  }

  const selected = discountNames
    .map((name) => discounts.find((discount) => discount.name === name))
    .filter((discount): discount is ServiceCatalogDiscount => !!discount);

  const perLine = largestOfType(selected, LINE_ITEM_DISCOUNT);
  const perOrder = largestOfType(selected, ORDER_DISCOUNT);

  const retail = service.prices.reduce((sum, price) => sum + price.unitAmount, 0);
  const subtotal = service.prices.reduce((sum, price) => sum + Math.max(price.unitAmount - perLine, 0), 0);
  const total = Math.max(subtotal - perOrder, 0);

  return { retail: roundToCents(retail), discount: roundToCents(retail - total), total: roundToCents(total) };
}

/**
 * Known backend refusals, in words a rep can act on.
 *
 * `bariendo-be`'s conflicts arrive as a whole sentence with two uuids in it,
 * prefixed by the method, path and status — `TreatmentPaymentsService.request`
 * builds it that way so a failure is debuggable, and it reached the drawer
 * verbatim (BAR-2525).
 *
 * Matched on the distinctive clause rather than the `(409)`: two different
 * conflicts share that status and need different sentences, and the status is
 * the part most likely to be re-mapped upstream. Anything unrecognised passes
 * through untouched — a rewritten unknown error is a debugging dead end.
 */
export function describePaymentError(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) {
    return fallback;
  }

  if (error.message.includes('already has a paid service checkout session')) {
    return 'This patient already has a payment order. Please edit the existing order instead.';
  }

  if (error.message.includes('refusing to change its selection')) {
    return 'This payment order already has money paid against it, so its services and discounts can no longer be changed.';
  }

  return error.message;
}

/** E.164: a `+`, a non-zero country digit, then 7–14 more. */
const E164 = /^\+[1-9]\d{7,14}$/;

/**
 * The number to text a payment link to, or `undefined` if there isn't one.
 *
 * **Normalises rather than refusing.** This used to demand that the stored value
 * already be E.164, because `send-sms-message` threads the resulting
 * `Communication` to the patient via `getResourceMatchesByPhone` — an exact-token
 * telecom search that a formatted number would miss, orphaning the record off the
 * patient.
 *
 * That reason is out of date: `getPhoneSearchCandidates` searches the normalised
 * value, the original, *and* the bare ten digits as an OR. Verified against
 * staging — `phone=+19998082726` alone returns 0 matches for a patient stored as
 * `9998082726`, while the comma-OR returns 1. Refusing therefore cost the option
 * on 11 of 200 recent patients for no protection, and a test in
 * `send-sms-message.test.ts` pins the candidate behaviour this now relies on.
 *
 * Prefers a `mobile` but falls back to any phone: plenty of records carry a
 * mobile under `use: 'home'`, and sending is a deliberate act either way.
 */
export function findTextableMobile(patient: Patient): string | undefined {
  const phones = (patient.telecom ?? [])
    .filter((point) => point.system === 'phone' && !!point.value)
    .map((point) => ({ use: point.use, value: normalizePhoneNumber(point.value!) }))
    // `normalizePhoneNumber` prepends `+` to anything it cannot place, so a
    // too-short number becomes a plausible-looking `+5551234`. Checking the
    // *result* is what keeps a real text from going to a number like that.
    .filter((point) => E164.test(point.value));

  return (phones.find((point) => point.use === 'mobile') ?? phones[0])?.value;
}

/**
 * Whether the Send/Resend control should exist at all.
 *
 * `sendTreatmentPaymentLink` throws `BadRequestException` when
 * `amount_total - amount_paid <= 0`, so offering the action on a settled order
 * produces a guaranteed error.
 *
 * Derived from `balance` rather than `status` deliberately: the balance is
 * server-derived arithmetic and settles immediately, while a status write can
 * lag behind it.
 */
export function canSendPaymentLink(order: TreatmentPaymentOrder | null): boolean {
  return !!order && order.balance > 0;
}

/**
 * Statuses where the leg's payment link is still payable.
 *
 * `DRAFT` is legacy in the payments DB but rows still carry it, so a leg in that
 * state is live for our purposes — treating it as dead would hide a link a
 * patient can still use.
 */
const LIVE_INTENT_STATUSES = new Set(['DRAFT', 'PENDING']);

export function isLiveLeg(intent: TreatmentPaymentOrderPaymentIntent): boolean {
  return LIVE_INTENT_STATUSES.has(intent.status);
}

/**
 * The payment page for a leg that already exists, so re-delivering a link never
 * mints a second one.
 *
 * **The URL shape is payment-frontend's, not ours.** `POST /payment-intent/stripe`
 * in `bariendo-payment-backend` builds it as
 * `${FRONTEND_DOMAIN}/${lang}/${stripePaymentIntentId}`, and the page is
 * `bariendo-payment-frontend/src/pages/[paymentIntentId]`. `processorRefId` *is*
 * that Stripe id. Composing it here duplicates that contract in a third repo —
 * accepted deliberately to keep this change in one repo, and pinned by a test so
 * a route change surfaces as a failure rather than as a dead link handed to a
 * patient.
 *
 * The locale segment is omitted on purpose. payment-frontend runs next-i18next
 * with `defaultLocale: 'en'` and locale detection on, so an unprefixed path is
 * routed to the patient's own language — better than Orbit guessing it from a
 * FHIR `communication` entry that may not match the bariendo user's
 * `app_language_code`.
 *
 * Stripe only: Cherry runs a financing application and texts the patient itself,
 * so there is no URL of ours to re-send. Dead legs return nothing — a cancelled
 * or declined intent's page cannot be paid.
 */
export function paymentLinkForLeg(
  intent: TreatmentPaymentOrderPaymentIntent,
  paymentFrontendUrl: string | undefined
): string | undefined {
  if (!paymentFrontendUrl || intent.processor !== 'STRIPE' || !intent.processorRefId || !isLiveLeg(intent)) {
    return undefined;
  }

  return `${paymentFrontendUrl.replace(/\/$/, '')}/${intent.processorRefId}`;
}

/**
 * What a method row offers, which is the whole answer to "why did resending mint
 * a second link".
 *
 * - A **live** leg is re-delivered, never re-issued: `POST /send-link` mints a
 *   fresh `PaymentIntent` on every call and nothing cancels the old one, so two
 *   sends leave two payable links against one balance.
 * - A **dead** leg (declined, cancelled) is replaced, which does mint — and is
 *   safe precisely because the leg it replaces can no longer be paid.
 * - A **settled** leg offers nothing. `Receipt` is in the design but Stripe's
 *   `receipt_url` reaches no layer Orbit can read: the ledger carries
 *   `chargeId`, amounts, processor refs and dates, and no receipt.
 */
export type MethodAction = 'copy' | 'resend' | 'replace';

export function methodActions(intent: TreatmentPaymentOrderPaymentIntent): MethodAction[] {
  if (isLiveLeg(intent)) {
    return ['copy', 'resend'];
  }

  return intent.status === 'FAILED' || intent.status === 'CANCELLED' ? ['replace'] : [];
}

/**
 * How long the order's oldest still-payable leg has gone unanswered, in days.
 *
 * Surfaced beside the balance (`$7,300 · 21 days`) so a stalling order is
 * visible without expanding anything. The *order's* age cannot do this job: an
 * order created months ago whose link went out yesterday is not stalling, and one
 * created yesterday with a dead 21-day-old leg is impossible.
 *
 * `undefined` when nothing is outstanding — there is no wait to report.
 */
export function oldestUnpaidLegAge(order: TreatmentPaymentOrder, now: Date): number | undefined {
  const live = order.paymentIntents.filter(isLiveLeg);

  if (live.length === 0) {
    return undefined;
  }

  const oldest = live.reduce((earliest, intent) => (intent.createdAt < earliest.createdAt ? intent : earliest));

  return Math.floor((now.getTime() - Date.parse(oldest.createdAt)) / DAY_MS);
}

/** Only two status tones exist on the timeline; the label carries the rest. */
export type MethodTone = 'settled' | 'needs-action';

export interface MethodState {
  label: string;
  tone: MethodTone;
}

/** A link with no answer for longer than this is a collections signal. */
const STALE_LINK_DAYS = 7;

const DAY_MS = 86_400_000;

/**
 * How one payment leg reads on card 01.
 *
 * The ageing is the point. Without it a leg says `Link sent` forever, so a dead
 * order looks identical to a healthy one — and the age of the oldest unpaid leg
 * is what tells a rep to chase.
 *
 * `Declined` beats ageing when both would apply: a declined leg needs replacing,
 * an unanswered one needs resending, and reporting the first as the second sends
 * the rep after the wrong thing.
 */
export function describeMethodState(
  intent: TreatmentPaymentOrderPaymentIntent,
  now: Date,
  /**
   * When the charge against this leg settled, from the ledger. Dates the `Paid`
   * label (`Paid · Jul 18`). Absent when the ledger could not be read — the
   * label then stands alone rather than borrowing the intent's `createdAt`,
   * which is when the link was made, not when the money arrived.
   */
  settledAt?: string
): MethodState {
  switch (intent.status) {
    case 'PAID':
      return { label: settledAt ? `Paid · ${formatDate(settledAt, 'MMM D')}` : 'Paid', tone: 'settled' };
    // No date: nothing in the projection records *when* a leg declined. The
    // intent's `createdAt` is when the link was minted, and putting that beside
    // "Declined" would report the wrong day on a financial row.
    case 'FAILED':
      return { label: 'Declined', tone: 'needs-action' };
    case 'CANCELLED':
      return { label: 'Cancelled', tone: 'needs-action' };
    case 'REFUNDED':
      return { label: 'Refunded', tone: 'needs-action' };
    default:
      break;
  }

  const days = Math.floor((now.getTime() - Date.parse(intent.createdAt)) / DAY_MS);

  return days > STALE_LINK_DAYS
    ? { label: `No response · ${days} days`, tone: 'needs-action' }
    : { label: 'Link sent', tone: 'settled' };
}

/** Rounds a dollar amount to the nearest cent, still in dollars. */
function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * Statuses that no longer hold any of the order against them.
 *
 * A cancelled or failed leg took no money and never will, so counting its
 * amount as allocated would make an order look fully covered when part of it has
 * nobody assigned to pay it. `REFUNDED` deliberately still counts: the money did
 * move, `amountPaid` still reflects it, and the return is reported separately as
 * `amountRefunded` — dropping it here would put allocation and balance out of
 * step in opposite directions.
 */
const DEAD_INTENT_STATUSES = new Set(['CANCELLED', 'FAILED']);

/**
 * How much of the order is already assigned to a payment method, in dollars.
 *
 * The figure the allocation line reads against `amountTotal`: an order split
 * $2,000 Stripe / $5,000 Cherry on a $9,300 total is $7,000 allocated, and the
 * $2,300 nobody has been asked for is the whole point of showing it.
 *
 * This is not `amountPaid`. Paid legs and merely-issued legs both hold their
 * share of the order; the difference between them is collections state, which
 * `describeMethodState` carries.
 */
export function sumAllocated(intents: TreatmentPaymentOrderPaymentIntent[]): number {
  return roundToCents(
    intents
      .filter((intent) => !DEAD_INTENT_STATUSES.has(intent.status))
      .reduce((total, intent) => total + intent.amount, 0)
  );
}

/**
 * What a newly added payment method should be pre-filled with, in dollars.
 *
 * The unallocated remainder, so adding a second rail to a part-covered order
 * lands on the figure nobody has been asked for. Falls back to the outstanding
 * balance when every dollar is already spoken for: at that point the reason to
 * add a method is that an existing leg went unanswered or was declined, and
 * `payment-backend` has no cancel endpoint, so replacing it means issuing the
 * balance again rather than editing the dead leg.
 *
 * Clamped to the balance because that is the ceiling `sendTreatmentPaymentLink`
 * enforces — a pre-fill it would refuse is a form that cannot be submitted.
 */
export function nextMethodAmount(order: TreatmentPaymentOrder): number {
  const unallocated = roundToCents(order.amountTotal - sumAllocated(order.paymentIntents));

  if (unallocated <= 0) {
    return Math.max(order.balance, 0);
  }

  return Math.min(unallocated, Math.max(order.balance, 0));
}

/**
 * Total refunded against an order, in dollars.
 *
 * **The only place this arithmetic should live.** Refunds have two
 * representations and getting it wrong misreports money on a financial card:
 *
 * - For Stripe charges `type` is always `PAYMENT`, and a refund appears as a
 *   non-zero `amountRefunded` on the original charge. Keying on
 *   `type === 'REFUND'` misses these — which is most refunds.
 * - CareCredit uses a multi-charge model instead: each refund is its own
 *   `REFUND` charge under the same payment leg and `amountRefunded` on the
 *   original stays zero, so several refund rows can share one leg. Cherry also
 *   writes `REFUND` rows. See `PAYMENT-PROCESSORS.md` in
 *   `bariendo-payment-backend/docs` for why the export forces this.
 * - A `REFUND` row and a non-zero `amountRefunded` **can describe the same
 *   money**, so adding them together double-counts it.
 *
 * Resolved per payment leg with `max`, not a sum: within a leg the two
 * representations alias each other, so the larger is the real figure. Across
 * legs they are genuinely different money and do add up.
 *
 * The failure mode this avoids is reporting $500 refunded against a $250 refund
 * — on the card a rep uses to decide whether a patient is owed money.
 */
export function sumRefunded(entries: TreatmentPaymentLedgerEntry[]): number {
  const byLeg = new Map<string, { fromRefundRows: number; fromAmountRefunded: number }>();

  for (const entry of entries) {
    const leg = byLeg.get(entry.paymentIntentId) ?? { fromRefundRows: 0, fromAmountRefunded: 0 };

    if (entry.type === LedgerEntryType.REFUND) {
      leg.fromRefundRows += entry.amount;
    }
    leg.fromAmountRefunded += entry.amountRefunded;

    byLeg.set(entry.paymentIntentId, leg);
  }

  let total = 0;
  for (const leg of byLeg.values()) {
    total += Math.max(leg.fromRefundRows, leg.fromAmountRefunded);
  }

  return roundToCents(total);
}

/**
 * Brand casing for the processors the payments DB records.
 *
 * The DB stores a screaming-snake enum, which is not a thing to show a CI. An
 * unknown value falls through to the raw string rather than to a placeholder: a
 * processor added to payment-backend before Orbit learns about it must still say
 * which rail took the money.
 */
const PROCESSOR_LABELS: Record<string, string> = {
  STRIPE: 'Stripe',
  CHERRY: 'Cherry',
  CARECREDIT: 'CareCredit',
  HEALTHIE: 'Healthie',
  OFFLINE: 'Offline',
  PATIENTFI: 'PatientFi',
};

export function formatProcessor(processor: string | undefined): string | undefined {
  if (!processor) {
    return undefined;
  }

  return PROCESSOR_LABELS[processor] ?? processor;
}

export interface ProcessorOption {
  value: string;
  label: string;
}

/**
 * Processors Orbit can *initiate* a payment on.
 *
 * Stripe returns a link. Cherry texts the patient and returns nothing.
 * CareCredit is deliberately absent — it has no create path in payment-backend
 * at all (`insertPaymentTransactionsFromCarecredit` is dead code; every
 * CareCredit charge arrives via an external `POST /charge`), so offering to send
 * one would expose a capability that does not exist.
 */
export const SENDABLE_PROCESSORS: ProcessorOption[] = [
  { value: 'STRIPE', label: 'Stripe' },
  { value: 'CHERRY', label: 'Cherry' },
];

/**
 * Processors whose settlements are entered by hand.
 *
 * Financed payments have no settlement webhook, so all of these land in the
 * payments DB only when someone records them. Stripe is absent because its
 * webhook does this automatically — a hand-entered Stripe charge would
 * double-post against the real one.
 */
export const RECORDABLE_PROCESSORS: ProcessorOption[] = [
  { value: 'CHERRY', label: 'Cherry' },
  { value: 'CARECREDIT', label: 'CareCredit' },
  // BAR-2984: offered from the PN consult's self-pay step (BAR-2923); no API.
  { value: 'PATIENTFI', label: 'PatientFi' },
];

/**
 * Why a Cherry send must be refused, or `undefined` to allow it (BAR-2604).
 *
 * Cherry is two phases and we automate only the second: the patient applies and
 * Cherry approves them for a limit, and only then can a practice charge against
 * it. Before this gate a rep could pick the Cherry rail, choose a practice, type
 * an amount and send against a patient Cherry had never heard of — and learn
 * about it as `Request failed with status code 502`, with Cherry's actual
 * sentence ("Patient not found, please create patient") destroyed in transit.
 *
 * **Fails open on purpose.** `unknown` and a missing status both allow the send.
 * A lookup that did not answer is not evidence that the patient is absent, and
 * refusing on our own outage would block charges Cherry would have taken — a
 * worse outcome than the one being prevented. The backend remains the real
 * guard; this only stops the sends that provably cannot succeed.
 *
 * See `docs/cherry.md`.
 */
export function cherrySendBlock(status: CherryAccountStatus | undefined): string | undefined {
  switch (status?.state) {
    case 'none':
      return 'This patient is not signed up with Cherry yet. Send them the Cherry application first.';
    case 'pending':
      return 'Cherry has not approved this patient yet — they have applied but have no spending limit.';
    case 'no-phone':
      return 'No mobile number on file, and Cherry identifies patients by phone.';
    case 'unusable':
      return status.reason;
    default:
      return undefined;
  }
}

/** How a Cherry status reads on a card, and how loudly. */
export interface CherryStatusDescription {
  label: string;
  /** `ok` approved, `blocked` cannot be charged, `caveat` unverified but allowed. */
  tone: 'ok' | 'blocked' | 'caveat';
}

/**
 * The one-line form of a Cherry status.
 *
 * `caveat` is a third tone rather than being folded into `blocked` because the
 * two mean opposite things to a rep: `blocked` says the send will fail, while
 * `caveat` says we could not find out and the send is still theirs to make.
 * Painting an unanswered lookup as a problem with the patient would send reps
 * chasing applications that already exist.
 */
export function describeCherryStatus(status: CherryAccountStatus | undefined): CherryStatusDescription | undefined {
  switch (status?.state) {
    case 'approved':
      return { label: `Cherry approved ${formatDollars(status.amount)}`, tone: 'ok' };
    case 'none':
      return { label: 'No Cherry account', tone: 'blocked' };
    case 'pending':
      return { label: 'Cherry application not approved yet', tone: 'blocked' };
    case 'no-phone':
      return { label: 'No mobile number on file', tone: 'blocked' };
    case 'unusable':
      return { label: `Cherry cannot charge this account: ${status.reason}`, tone: 'blocked' };
    case 'unknown':
      return { label: `Could not check Cherry: ${status.reason}`, tone: 'caveat' };
    default:
      return undefined;
  }
}
